// @ts-nocheck
import React, { useState, useCallback, useMemo, useEffect, memo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  Calendar, 
  Clock, 
  Gavel, 
  TrendingUp, 
  Filter,
  Grid3x3,
  List,
  ChevronDown,
  Sparkles,
  AlertCircle,
  Eye,
  ArrowUpRight,
  Timer,
  DollarSign,
  Palette
} from 'lucide-react';
import { formatToEST, formatCurrency } from '@/utils/dateUtils';
import { useAuth } from '@/providers/AuthProvider';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
// motion is kept for the countdown timer but AnimatePresence removed for performance

interface ArtworkDetails {
  id: string;
  title: string;
  image_url: string;
  additional_images: string[];
  medium: string;
  dimensions: string;
  frame_dimensions?: string;
  year: number;
  description: string;
  estimate_low: number;
  estimate_high: number;
  condition?: string;
  provenance?: string;
  signature?: string;
  artist: {
    name: string;
  };
}

interface LotDetails {
  id: string;
  lot_number: number;
  starting_bid: number;
  current_bid: number;
  status: string;
  artworks: ArtworkDetails;
  auction_id: string;
}

interface AuctionWithLots {
  id: string;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  status: string;
  location: string;
  image_url: string;
  lots: LotDetails[];
}

interface ProcessedLot extends LotDetails {
  auctionTitle: string;
  auctionStatus: string;
  auctionDate: string;
  auctionId: string;
  nextBidAmount: number;
  incrementAmount: number;
}

type ViewMode = 'grid' | 'list';
type SortMode = 'lot-number' | 'price-low' | 'price-high' | 'date' | 'artist';

// Default bid increment rules - used for instant calculation without DB call
const DEFAULT_BID_INCREMENTS = [
  { min_amount: 0, max_amount: 100, increment: 10 },
  { min_amount: 100, max_amount: 500, increment: 25 },
  { min_amount: 500, max_amount: 1000, increment: 50 },
  { min_amount: 1000, max_amount: 2500, increment: 100 },
  { min_amount: 2500, max_amount: 5000, increment: 250 },
  { min_amount: 5000, max_amount: 10000, increment: 500 },
  { min_amount: 10000, max_amount: 25000, increment: 1000 },
  { min_amount: 25000, max_amount: 50000, increment: 2500 },
  { min_amount: 50000, max_amount: null, increment: 5000 },
];

// Synchronous bid increment calculation - no DB calls
function calculateBidIncrement(amount: number, cachedRules?: typeof DEFAULT_BID_INCREMENTS): { nextBid: number; increment: number } {
  const rules = cachedRules || DEFAULT_BID_INCREMENTS;
  const sortedRules = [...rules].sort((a, b) => b.min_amount - a.min_amount);
  const rule = sortedRules.find(r => 
    amount >= r.min_amount && (r.max_amount === null || amount < r.max_amount)
  );
  const increment = rule?.increment || Math.max(50, Math.round(amount * 0.1));
  return { nextBid: amount + increment, increment };
}

export default function AdvancedUpcomingAuctions() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedLot, setSelectedLot] = useState<ProcessedLot | null>(null);
  const [selectedAuction, setSelectedAuction] = useState<AuctionWithLots | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [sortMode, setSortMode] = useState<SortMode>('date');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 50000]);
  const [selectedArtist, setSelectedArtist] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Prefetch bid increments for synchronous use
  const { data: bidIncrementRules } = useQuery({
    queryKey: ['bidIncrements'],
    queryFn: async () => {
      const { data } = await supabase
        .from('bid_increments')
        .select('min_amount, max_amount, increment')
        .order('min_amount', { ascending: true });
      return data || DEFAULT_BID_INCREMENTS;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
  });

  // Optimized single query - fetch all lots for upcoming auctions at once
  const { data: upcomingAuctionsWithLots, isLoading, refetch } = useQuery({
    queryKey: ['advancedUpcomingAuctionLots'],
    queryFn: async () => {
      // Single optimized query - fetch lots with auction data joined
      const { data: lots, error } = await supabase
        .from('lots')
        .select(`
          id,
          lot_number,
          starting_bid,
          current_bid,
          status,
          auction_id,
          auctions!inner (
            id,
            title,
            description,
            start_date,
            end_date,
            status,
            location,
            image_url
          ),
          artworks (
            id,
            title,
            image_url,
            additional_images,
            medium,
            dimensions,
            frame_dimensions,
            year,
            description,
            estimate_low,
            estimate_high,
            artist:artist_id (
              name
            )
          )
        `)
        .eq('auctions.status', 'upcoming')
        .order('lot_number')
        .limit(40);

      if (error || !lots) {
        console.log('❌ No upcoming auction lots found');
        return [];
      }

      // Group lots by auction
      const auctionMap = new Map<string, AuctionWithLots>();
      
      for (const lot of lots) {
        if (!lot.artworks?.image_url || !lot.auctions) continue;
        
        const auctionId = lot.auctions.id;
        if (!auctionMap.has(auctionId)) {
          auctionMap.set(auctionId, {
            ...lot.auctions,
            lots: []
          });
        }
        
        // Remove the nested auctions from lot to match interface
        const { auctions: _, ...lotWithoutAuction } = lot;
        auctionMap.get(auctionId)!.lots.push(lotWithoutAuction);
      }

      // Sort by start_date and limit to 5 auctions
      return Array.from(auctionMap.values())
        .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())
        .slice(0, 5);
    },
    staleTime: 30000, // Data is fresh for 30 seconds - shows cached data instantly
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchInterval: 30000,
  });

  // Subscribe to real-time bid updates
  useEffect(() => {
    const channel = supabase
      .channel('upcoming-lots-bids')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bids',
        },
        () => {
          // Invalidate cache on bid update
          queryClient.invalidateQueries({ queryKey: ['advancedUpcomingAuctionLots'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  // Process lots synchronously - no async, no extra renders
  const enrichedLots = useMemo(() => {
    if (!upcomingAuctionsWithLots || upcomingAuctionsWithLots.length === 0) return [];
    
    const rules = bidIncrementRules || DEFAULT_BID_INCREMENTS;
    
    return upcomingAuctionsWithLots.flatMap(auction => 
      auction.lots.map(lot => {
        const currentBid = lot.current_bid || lot.starting_bid;
        const { nextBid, increment } = calculateBidIncrement(currentBid, rules);
        
        return {
          ...lot,
          auctionTitle: auction.title,
          auctionStatus: auction.status,
          auctionDate: auction.start_date,
          auctionId: auction.id,
          nextBidAmount: nextBid,
          incrementAmount: increment
        };
      })
    );
  }, [upcomingAuctionsWithLots, bidIncrementRules]);

  // Get unique artists for filtering
  const uniqueArtists = useMemo(() => {
    const artists = new Set(enrichedLots.map(lot => lot.artworks.artist.name));
    return Array.from(artists).sort();
  }, [enrichedLots]);

  // Apply filters and sorting
  const filteredAndSortedLots = useMemo(() => {
    let filtered = [...enrichedLots];

    // Artist filter
    if (selectedArtist !== 'all') {
      filtered = filtered.filter(lot => lot.artworks.artist.name === selectedArtist);
    }

    // Price range filter
    filtered = filtered.filter(lot => {
      const currentBid = lot.current_bid || lot.starting_bid;
      return currentBid >= priceRange[0] && currentBid <= priceRange[1];
    });

    // Sorting
    switch (sortMode) {
      case 'lot-number':
        filtered.sort((a, b) => a.lot_number - b.lot_number);
        break;
      case 'price-low':
        filtered.sort((a, b) => (a.current_bid || a.starting_bid) - (b.current_bid || b.starting_bid));
        break;
      case 'price-high':
        filtered.sort((a, b) => (b.current_bid || b.starting_bid) - (a.current_bid || a.starting_bid));
        break;
      case 'date':
        filtered.sort((a, b) => new Date(a.auctionDate).getTime() - new Date(b.auctionDate).getTime());
        break;
      case 'artist':
        filtered.sort((a, b) => a.artworks.artist.name.localeCompare(b.artworks.artist.name));
        break;
    }

    return filtered;
  }, [enrichedLots, selectedArtist, priceRange, sortMode]);

  const handleLotClick = useCallback((lot: ProcessedLot) => {
    setSelectedLot(lot);
    const auction = upcomingAuctionsWithLots?.find(a => a.id === lot.auctionId);
    setSelectedAuction(auction || null);
  }, [upcomingAuctionsWithLots]);

  // Countdown Timer Component
  const CountdownTimer = ({ targetDate }: { targetDate: string }) => {
    const [timeLeft, setTimeLeft] = useState('');

    useEffect(() => {
      const calculateTimeLeft = () => {
        const now = new Date().getTime();
        const target = new Date(targetDate).getTime();
        const difference = target - now;

        if (difference <= 0) {
          setTimeLeft('Starting soon');
          return;
        }

        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

        if (days > 0) {
          setTimeLeft(`${days}d ${hours}h`);
        } else if (hours > 0) {
          setTimeLeft(`${hours}h ${minutes}m`);
        } else {
          setTimeLeft(`${minutes}m`);
        }
      };

      calculateTimeLeft();
      const interval = setInterval(calculateTimeLeft, 60000); // Update every minute

      return () => clearInterval(interval);
    }, [targetDate]);

    return (
      <div className="flex items-center gap-1.5 text-xs">
        <Timer className="h-3.5 w-3.5 text-amber-400 animate-pulse" />
        <span className="text-amber-300 font-medium">{timeLeft}</span>
      </div>
    );
  };

  if (isLoading && enrichedLots.length === 0) {
    return (
      <section className="py-24 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          {/* Header skeleton */}
          <div className="text-center mb-16">
            <div className="inline-block h-10 w-48 bg-slate-800/40 rounded-full mb-6 animate-pulse"></div>
            <div className="h-16 w-80 bg-slate-800/30 rounded-lg mx-auto mb-3 animate-pulse"></div>
            <div className="h-12 w-64 bg-slate-800/30 rounded-lg mx-auto mb-6 animate-pulse"></div>
            <div className="h-6 max-w-lg bg-slate-800/20 rounded-lg mx-auto animate-pulse"></div>
          </div>
          
          {/* Cards skeleton - shows immediately */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-slate-900/40 rounded-2xl overflow-hidden border border-slate-800/50">
                <div className="h-48 bg-slate-800/30 animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-5 w-3/4 bg-slate-800/40 rounded animate-pulse" />
                  <div className="h-4 w-1/2 bg-slate-800/30 rounded animate-pulse" />
                  <div className="space-y-2 pt-3">
                    <div className="h-4 w-full bg-slate-800/20 rounded animate-pulse" />
                    <div className="h-4 w-full bg-slate-800/20 rounded animate-pulse" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (filteredAndSortedLots.length === 0 && enrichedLots.length === 0) {
    return null; // Don't show section if no upcoming lots
  }

  return (
    <>
      <section className="relative py-32 overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        {/* Animated Background Effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-32 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          {/* Premium Header Section - Optimized for fast initial paint */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center gap-3 px-6 py-3 mb-10 bg-gradient-to-r from-blue-500/10 via-emerald-500/10 to-teal-500/10 backdrop-blur-xl rounded-full border border-white/20 shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-500">
              <Sparkles className="h-5 w-5 text-emerald-400 animate-pulse" />
              <span className="text-white/90 font-light tracking-wider text-sm uppercase">Live Auction Preview</span>
              <Gavel className="h-5 w-5 text-blue-400" />
            </div>
            
            <h2 className="text-6xl md:text-7xl lg:text-8xl font-extralight mb-8 leading-tight animate-in fade-in slide-in-from-bottom-4 duration-500 delay-75">
              <span className="block text-white tracking-tight">Upcoming</span>
              <span className="block bg-gradient-to-r from-blue-400 via-emerald-400 to-teal-400 bg-clip-text text-transparent font-light">
                Live Bidding
              </span>
            </h2>
            
            <p className="text-xl md:text-2xl text-white/70 max-w-4xl mx-auto leading-relaxed font-light animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100">
              Discover exceptional <span className="text-emerald-300 font-normal">Florida Highwaymen artwork</span> with 
              <span className="text-blue-300 font-normal"> real-time bid tracking</span> and accurate increment calculations
            </p>
          </div>

          {/* Advanced Controls */}
          <div className="mb-12 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-150">
            <Card className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 shadow-2xl">
              <div className="p-6">
                <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
                  {/* Left Side - View & Filter Toggle */}
                  <div className="flex items-center gap-4 flex-wrap">
                    {/* View Mode Toggle */}
                    <div className="flex items-center gap-2 bg-slate-800/60 rounded-xl p-1 border border-slate-700/50">
                      <Button
                        variant={viewMode === 'grid' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setViewMode('grid')}
                        className={`${viewMode === 'grid' ? 'bg-gradient-to-r from-blue-600 to-emerald-600' : 'text-white/70 hover:text-white'}`}
                      >
                        <Grid3x3 className="h-4 w-4 mr-2" />
                        Grid
                      </Button>
                      <Button
                        variant={viewMode === 'list' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setViewMode('list')}
                        className={`${viewMode === 'list' ? 'bg-gradient-to-r from-blue-600 to-emerald-600' : 'text-white/70 hover:text-white'}`}
                      >
                        <List className="h-4 w-4 mr-2" />
                        List
                      </Button>
                    </div>

                    {/* Filter Toggle */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowFilters(!showFilters)}
                      className="border-blue-400/30 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 hover:border-blue-400/50 hover:text-blue-200"
                    >
                      <Filter className="h-4 w-4 mr-2" />
                      {showFilters ? 'Hide' : 'Show'} Filters
                      <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                    </Button>

                    {/* Results Count */}
                    <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-4 py-2">
                      {filteredAndSortedLots.length} {filteredAndSortedLots.length === 1 ? 'Lot' : 'Lots'} Available
                    </Badge>
                  </div>

                  {/* Right Side - Sort */}
                  <div className="flex items-center gap-3">
                    <span className="text-white/60 text-sm font-light">Sort by:</span>
                    <Select value={sortMode} onValueChange={(value) => setSortMode(value as SortMode)}>
                      <SelectTrigger className="w-[180px] bg-slate-800/60 border-slate-700/50 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-white/20 text-white">
                        <SelectGroup>
                          <SelectLabel className="text-white/60">Sort Options</SelectLabel>
                          <SelectItem value="date">Auction Date</SelectItem>
                          <SelectItem value="lot-number">Lot Number</SelectItem>
                          <SelectItem value="price-low">Price: Low to High</SelectItem>
                          <SelectItem value="price-high">Price: High to Low</SelectItem>
                          <SelectItem value="artist">Artist Name</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Expandable Filters - CSS transition for faster render */}
                {showFilters && (
                  <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="pt-6 mt-6 border-t border-white/10">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Artist Filter */}
                        <div>
                          <label className="text-white/80 text-sm font-light mb-3 block">Filter by Artist</label>
                          <Select value={selectedArtist} onValueChange={setSelectedArtist}>
                            <SelectTrigger className="bg-slate-800/60 border-slate-700/50 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-900 border-white/20 text-white max-h-[300px]">
                              <SelectGroup>
                                <SelectItem value="all">All Artists</SelectItem>
                                {uniqueArtists.map(artist => (
                                  <SelectItem key={artist} value={artist}>{artist}</SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Price Range Filter */}
                        <div>
                          <label className="text-white/80 text-sm font-light mb-3 block">
                            Price Range: {formatCurrency(priceRange[0])} - {formatCurrency(priceRange[1])}
                          </label>
                          <Slider
                            value={priceRange}
                            onValueChange={(value) => setPriceRange(value as [number, number])}
                            max={50000}
                            min={0}
                            step={100}
                            className="mt-2"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Lots Display */}
          {filteredAndSortedLots.length === 0 ? (
            <div className="text-center py-20 animate-in fade-in duration-300">
              <Card className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 shadow-2xl max-w-2xl mx-auto p-12">
                <AlertCircle className="h-16 w-16 text-blue-400 mx-auto mb-6" />
                <h3 className="text-2xl font-light text-white mb-4">No Lots Found</h3>
                <p className="text-white/70 font-light">
                  Try adjusting your filters to see more results.
                </p>
                <Button
                  onClick={() => {
                    setSelectedArtist('all');
                    setPriceRange([0, 50000]);
                  }}
                  className="mt-6 bg-gradient-to-r from-blue-600 to-emerald-600"
                >
                  Reset Filters
                </Button>
              </Card>
            </div>
          ) : (
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' 
              : 'flex flex-col gap-4'
            }>
              {filteredAndSortedLots.map((lot, index) => (
                <div
                  key={lot.id}
                  className="animate-in fade-in slide-in-from-bottom-4 duration-300"
                  style={{ animationDelay: `${Math.min(index * 30, 150)}ms` }}
                >
                  {viewMode === 'grid' ? (
                    <LotCardGrid lot={lot} onClick={() => handleLotClick(lot)} />
                  ) : (
                    <LotCardList lot={lot} onClick={() => handleLotClick(lot)} />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Call to Action */}
          {filteredAndSortedLots.length > 0 && (
            <div className="text-center mt-20 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-200">
              <div className="relative group max-w-3xl mx-auto">
                {/* Apple-style 3D Glass Effect - NO GREY */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/30 via-emerald-500/30 to-teal-500/30 rounded-[32px] blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-blue-400/10 to-emerald-400/10 rounded-[32px] opacity-60"></div>
                
                <Card className="relative bg-gradient-to-br from-slate-950/60 via-blue-950/50 to-emerald-950/50 backdrop-blur-3xl border border-blue-400/30 shadow-[0_8px_32px_0_rgba(59,130,246,0.3)] p-12 rounded-[32px] overflow-hidden">
                  {/* Vibrant inner glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-emerald-500/10 to-teal-500/10 rounded-[32px]"></div>
                  
                  {/* Glass reflection effect */}
                  <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-blue-400/15 to-transparent rounded-t-[32px] opacity-60"></div>
                  
                  {/* Content - Glass Transparent Style */}
                  <div className="relative z-10">
                    <div className="relative inline-block mb-6">
                      <div className="absolute inset-0 bg-emerald-400/20 blur-xl rounded-full"></div>
                      <Gavel className="relative h-16 w-16 text-emerald-400 mx-auto drop-shadow-[0_0_20px_rgba(52,211,153,0.6)]" />
                    </div>
                    
                    <h3 className="text-3xl font-extralight text-white/90 mb-4 tracking-wide drop-shadow-[0_2px_10px_rgba(0,0,0,0.3)]">
                      {user ? "Ready to Explore More?" : "Join the Auction"}
                    </h3>
                    
                    <p className="text-xl text-white/70 mb-8 font-extralight leading-relaxed max-w-2xl mx-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.25)]">
                      {user 
                        ? "View complete auction catalogs, set up proxy bids, and never miss a rare Florida Highwaymen masterpiece."
                        : "Register now to participate in our upcoming auctions and gain access to exclusive bidding opportunities."
                      }
                    </p>
                    
                    <Link to={user ? "/auctions" : "/login"}>
                      <Button className="relative bg-white/10 backdrop-blur-md hover:bg-white/20 border border-white/30 hover:border-white/50 text-white font-light px-10 py-6 text-lg shadow-[0_8px_30px_rgba(0,0,0,0.3)] hover:shadow-[0_8px_40px_rgba(52,211,153,0.3)] rounded-2xl transition-all duration-300 hover:scale-105 overflow-hidden group/btn">
                        {/* Animated gradient overlay */}
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-emerald-500/20 to-teal-500/0 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                        
                        {/* Button shimmer effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-1000"></div>
                        
                        <span className="relative flex items-center drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]">
                          {user ? (
                            <>
                              <Eye className="mr-3 h-5 w-5" />
                              View All Auctions
                              <ArrowUpRight className="ml-3 h-5 w-5" />
                            </>
                          ) : (
                            <>
                              <Gavel className="mr-3 h-5 w-5" />
                              Register to Bid
                              <ArrowUpRight className="ml-3 h-5 w-5" />
                            </>
                          )}
                        </span>
                      </Button>
                    </Link>
                  </div>
                  
                  {/* Bottom edge highlight */}
                  <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent"></div>
                </Card>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Enhanced Lot Details Modal */}
      <Dialog open={!!selectedLot} onOpenChange={() => setSelectedLot(null)}>
        <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto bg-slate-950/95 backdrop-blur-2xl border-slate-700/50 text-white shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-3xl font-light text-white mb-2 flex items-center gap-3">
              <Badge className="bg-blue-600/90 text-white">Lot #{selectedLot?.lot_number}</Badge>
              {selectedLot?.artworks?.title}
            </DialogTitle>
            {selectedAuction && (
              <div className="flex items-center gap-4 text-sm text-white/60">
                <span className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-emerald-400" />
                  {formatToEST(selectedAuction.start_date)}
                </span>
                <CountdownTimer targetDate={selectedAuction.start_date} />
              </div>
            )}
          </DialogHeader>
          
          {selectedLot && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-6">
              {/* Image Section */}
              <div className="space-y-4">
                <div className="relative aspect-square rounded-2xl overflow-hidden bg-slate-900/60 border border-slate-700/50 shadow-2xl">
                  <img 
                    src={selectedLot.artworks.image_url} 
                    alt={selectedLot.artworks.title}
                    className="w-full h-full object-contain"
                  />
                </div>
                {selectedLot.artworks.additional_images && selectedLot.artworks.additional_images.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {selectedLot.artworks.additional_images.slice(0, 4).map((img, idx) => (
                      <div key={idx} className="aspect-square rounded-lg overflow-hidden bg-slate-900/60 border border-slate-700/50">
                        <img src={img} alt={`View ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Details Section */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-light mb-3 text-white">{selectedLot.artworks.title}</h3>
                  <p className="text-xl text-blue-300 mb-6">by {selectedLot.artworks.artist.name}</p>
                  <p className="text-white/80 leading-relaxed font-light text-lg">{selectedLot.artworks.description}</p>
                </div>

                {/* Bid Information - Enhanced with Dark Theme */}
                <div className="grid grid-cols-1 gap-4">
                  {/* Current Bid Card */}
                  <Card className="relative bg-slate-900/60 border border-slate-700/50 p-6 shadow-xl overflow-hidden group hover:border-blue-500/40 transition-all duration-300">
                    {/* Subtle gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-50"></div>
                    
                    {/* Content */}
                    <div className="relative">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-light text-white/60 text-xs uppercase tracking-wider">Current Bid</h4>
                        <div className="bg-blue-500/10 p-2 rounded-lg">
                          <DollarSign className="h-5 w-5 text-blue-400" />
                        </div>
                      </div>
                      <p className="text-5xl font-extralight text-white mb-1">
                        {formatCurrency(selectedLot.current_bid || selectedLot.starting_bid)}
                      </p>
                      <div className="h-1 w-16 bg-gradient-to-r from-blue-500 to-transparent rounded-full"></div>
                    </div>
                  </Card>
                  
                  {/* Next Minimum Bid Card */}
                  <Card className="relative bg-slate-900/60 border border-slate-700/50 p-6 shadow-xl overflow-hidden group hover:border-emerald-500/40 transition-all duration-300">
                    {/* Subtle gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent opacity-50"></div>
                    
                    {/* Content */}
                    <div className="relative">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-light text-white/60 text-xs uppercase tracking-wider">Next Minimum Bid</h4>
                        <div className="bg-emerald-500/10 p-2 rounded-lg">
                          <TrendingUp className="h-5 w-5 text-emerald-400" />
                        </div>
                      </div>
                      <p className="text-5xl font-extralight text-emerald-300 mb-2">
                        {formatCurrency(selectedLot.nextBidAmount)}
                      </p>
                      <div className="flex items-center gap-2 text-sm">
                        <div className="h-1 w-12 bg-gradient-to-r from-emerald-500 to-transparent rounded-full"></div>
                        <span className="text-white/50">Increment:</span>
                        <span className="text-emerald-400 font-medium">{formatCurrency(selectedLot.incrementAmount)}</span>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Comprehensive Artwork Details Grid */}
                <div className="space-y-4">
                  <h4 className="font-light text-white/80 uppercase tracking-wider text-sm mb-4">Complete Lot Information</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Medium */}
                    <Card className="bg-slate-900/60 border border-slate-700/50 p-4 hover:border-blue-500/30 transition-colors">
                      <div className="flex items-center gap-2 mb-2">
                        <Palette className="h-4 w-4 text-blue-400" />
                        <span className="text-white/60 text-xs uppercase tracking-wider">Medium</span>
                      </div>
                      <p className="text-white font-light">{selectedLot.artworks.medium || 'Not specified'}</p>
                    </Card>
                    
                    {/* Canvas/Image Dimensions */}
                    <Card className="bg-slate-900/60 border border-slate-700/50 p-4 hover:border-blue-500/30 transition-colors">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="h-4 w-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                        </svg>
                        <span className="text-white/60 text-xs uppercase tracking-wider">Dimensions</span>
                      </div>
                      <p className="text-white font-light">{selectedLot.artworks.dimensions || 'Not specified'}</p>
                    </Card>
                    
                    {/* Frame Dimensions - Always Show */}
                    <Card className="bg-slate-900/60 border border-slate-700/50 p-4 hover:border-emerald-500/30 transition-colors">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="h-4 w-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                        </svg>
                        <span className="text-white/60 text-xs uppercase tracking-wider">Frame Dimensions</span>
                      </div>
                      <p className="text-white font-light">{selectedLot.artworks.frame_dimensions || 'Not specified'}</p>
                    </Card>
                    
                    {/* Year */}
                    <Card className="bg-slate-900/60 border border-slate-700/50 p-4 hover:border-blue-500/30 transition-colors">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="h-4 w-4 text-blue-400" />
                        <span className="text-white/60 text-xs uppercase tracking-wider">Year Created</span>
                      </div>
                      <p className="text-white font-light">{selectedLot.artworks.year || 'Unknown'}</p>
                    </Card>
                    
                    {/* Estimate */}
                    <Card className="bg-slate-900/60 border border-slate-700/50 p-4 hover:border-blue-500/30 transition-colors">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="h-4 w-4 text-blue-400" />
                        <span className="text-white/60 text-xs uppercase tracking-wider">Estimate</span>
                      </div>
                      <p className="text-white font-light">
                        {formatCurrency(selectedLot.artworks.estimate_low)} - {formatCurrency(selectedLot.artworks.estimate_high)}
                      </p>
                    </Card>
                  </div>
                  
                  {/* Additional Optional Details */}
                  
                  {/* Condition */}
                  {selectedLot.artworks.condition && (
                    <Card className="bg-slate-900/60 border border-slate-700/50 p-4 hover:border-blue-500/30 transition-colors">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="h-4 w-4 text-emerald-400" />
                        <span className="text-white/60 text-xs uppercase tracking-wider">Condition</span>
                      </div>
                      <p className="text-white font-light">{selectedLot.artworks.condition}</p>
                    </Card>
                  )}
                  
                  {/* Provenance */}
                  {selectedLot.artworks.provenance && (
                    <Card className="bg-slate-900/60 border border-slate-700/50 p-4 hover:border-blue-500/30 transition-colors">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="h-4 w-4 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="text-white/60 text-xs uppercase tracking-wider">Provenance</span>
                      </div>
                      <p className="text-white font-light text-sm">{selectedLot.artworks.provenance}</p>
                    </Card>
                  )}
                  
                  {/* Signature */}
                  {selectedLot.artworks.signature && (
                    <Card className="bg-slate-900/60 border border-slate-700/50 p-4 hover:border-blue-500/30 transition-colors">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="h-4 w-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                        <span className="text-white/60 text-xs uppercase tracking-wider">Signature</span>
                      </div>
                      <p className="text-white font-light">{selectedLot.artworks.signature}</p>
                    </Card>
                  )}
                </div>

                {/* Auction Details */}
                {selectedAuction && (
                  <Card className="bg-slate-900/60 border border-slate-700/50 p-6">
                    <h4 className="font-light text-white/80 mb-3 uppercase tracking-wider text-sm">Auction Details</h4>
                    <p className="text-white font-light text-lg mb-2">{selectedAuction.title}</p>
                    <div className="flex items-center gap-4 text-sm text-white/70">
                      <span className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {formatToEST(selectedAuction.start_date)}
                      </span>
                      {selectedAuction.location && (
                        <span>• {selectedAuction.location}</span>
                      )}
                    </div>
                  </Card>
                )}

                {/* Action Button */}
                <Link to={`/auctions/${selectedLot.auctionId}`} className="block">
                  <Button className="w-full bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-500 hover:to-emerald-500 text-white py-6 text-lg rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300">
                    <Eye className="mr-3 h-5 w-5" />
                    View Full Auction
                    <ArrowUpRight className="ml-3 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// Grid View Lot Card Component - Optimized with CSS transitions
const LotCardGrid = memo(function LotCardGrid({ lot, onClick }: { lot: ProcessedLot; onClick: () => void }) {
  const currentBid = lot.current_bid || lot.starting_bid;

  return (
    <div
      onClick={onClick}
      className="cursor-pointer group transform hover:-translate-y-2 hover:scale-[1.02] transition-transform duration-300"
    >
      <Card className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 shadow-lg hover:shadow-2xl hover:border-emerald-500/40 transition-all duration-300 h-full flex flex-col overflow-hidden">
        {/* Image */}
        <div className="relative h-64 overflow-hidden">
          <img 
            src={lot.artworks.image_url} 
            alt={lot.artworks.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          {/* Badges */}
          <div className="absolute top-3 left-3 right-3 flex justify-between items-start">
            <Badge className="bg-blue-600/95 backdrop-blur-sm text-white border-0 font-light shadow-lg">
              Lot #{lot.lot_number}
            </Badge>
            <Badge className="bg-emerald-600/95 backdrop-blur-sm text-white border-0 font-light shadow-lg">
              <Clock className="h-3 w-3 mr-1" />
              Upcoming
            </Badge>
          </div>

          {/* Hover Overlay */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="bg-slate-900/80 backdrop-blur-md rounded-full p-4 border border-emerald-400/50">
              <Eye className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 flex-grow flex flex-col">
          <div className="mb-4">
            <h3 className="text-lg font-light mb-2 line-clamp-2 text-white tracking-wide group-hover:text-emerald-300 transition-colors">
              {lot.artworks.title}
            </h3>
            <p className="text-blue-300 text-sm font-light">
              by {lot.artworks.artist.name}
            </p>
          </div>

          {/* Bid Information */}
          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between py-2 border-b border-white/10">
              <span className="text-white/60 text-xs uppercase tracking-wider font-light">Current Bid</span>
              <span className="text-white font-light text-lg">
                {formatCurrency(currentBid)}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-white/10">
              <span className="text-white/60 text-xs uppercase tracking-wider font-light">Next Bid</span>
              <span className="text-emerald-400 font-normal text-lg">
                {formatCurrency(lot.nextBidAmount)}
              </span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-white/60 text-xs uppercase tracking-wider font-light">Increment</span>
              <span className="text-blue-400 font-light">
                +{formatCurrency(lot.incrementAmount)}
              </span>
            </div>
          </div>

          {/* Footer Info */}
          <div className="mt-auto pt-4 border-t border-white/10">
            <div className="flex items-center justify-between text-xs text-white/50 font-light">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-emerald-400" />
                <span>{new Date(lot.auctionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <TrendingUp className="h-3.5 w-3.5 text-blue-400" />
                <span className="truncate">
                  Est. {lot.artworks.estimate_low && lot.artworks.estimate_high 
                    ? `${formatCurrency(lot.artworks.estimate_low)} - ${formatCurrency(lot.artworks.estimate_high)}`
                    : formatCurrency(lot.artworks.estimate_low || 0)
                  }
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
});

// List View Lot Card Component - Optimized with CSS transitions
const LotCardList = memo(function LotCardList({ lot, onClick }: { lot: ProcessedLot; onClick: () => void }) {
  const currentBid = lot.current_bid || lot.starting_bid;

  return (
    <div
      onClick={onClick}
      className="cursor-pointer group transform hover:translate-x-1 transition-transform duration-200"
    >
      <Card className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 hover:border-emerald-500/40 shadow-lg hover:shadow-2xl transition-all duration-300">
        <div className="p-6 flex flex-col md:flex-row gap-6 items-start md:items-center">
          {/* Image */}
          <div className="relative w-full md:w-40 h-40 flex-shrink-0 rounded-xl overflow-hidden bg-slate-900/60 border border-slate-700/50">
            <img 
              src={lot.artworks.image_url} 
              alt={lot.artworks.title}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              loading="lazy"
            />
            <Badge className="absolute top-2 left-2 bg-blue-600/95 backdrop-blur-sm text-white border-0 font-light text-xs">
              Lot #{lot.lot_number}
            </Badge>
          </div>

          {/* Info */}
          <div className="flex-grow grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
            {/* Artwork Details */}
            <div className="md:col-span-2">
              <h3 className="text-xl font-light mb-1 text-white group-hover:text-emerald-300 transition-colors">
                {lot.artworks.title}
              </h3>
              <p className="text-blue-300 text-sm mb-2">by {lot.artworks.artist.name}</p>
              <div className="flex items-center gap-3 text-xs text-white/50">
                <span>{lot.artworks.medium}</span>
                <span>•</span>
                <span>{lot.artworks.year}</span>
              </div>
            </div>

            {/* Bid Info */}
            <div>
              <span className="text-white/60 text-xs uppercase tracking-wider block mb-1">Current Bid</span>
              <span className="text-white font-light text-xl">{formatCurrency(currentBid)}</span>
            </div>

            <div>
              <span className="text-white/60 text-xs uppercase tracking-wider block mb-1">Next Bid</span>
              <span className="text-emerald-400 font-normal text-xl">{formatCurrency(lot.nextBidAmount)}</span>
              <span className="text-blue-400 text-xs block mt-1">+{formatCurrency(lot.incrementAmount)}</span>
            </div>

            {/* Auction Date */}
            <div className="flex items-center justify-between md:justify-end gap-4">
              <div>
                <span className="text-white/60 text-xs uppercase tracking-wider block mb-1">Auction</span>
                <span className="text-white/80 text-sm flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-emerald-400" />
                  {new Date(lot.auctionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              </div>
              <ArrowUpRight className="h-6 w-6 text-blue-400 group-hover:text-emerald-400 transition-colors" />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
});

